import { apiClient } from './client'

export interface LoginRequest {
  email: string
  password: string
  org_slug?: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  user: {
    id: string
    email: string
    name: string
    role: string
    timezone: string
    avatar_url?: string
    org_id: string
    org_slug?: string
  }
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
  invite_token: string
}

export interface OrgOption {
  org_slug: string
  org_name: string
  role: string
  icon_url?: string | null
}

export interface SlackAuthResult {
  status: 'authenticated' | 'select_org' | 'platform'
  access_token?: string | null
  user?: LoginResponse['user'] | null
  must_set_password?: boolean
  selection_token?: string | null
  organizations?: OrgOption[] | null
}

export interface OrgInfo {
  slug: string
  name: string
  has_slack_login: boolean
}

export interface RegisterOrgRequest {
  org_name: string
  org_slug: string
  admin_name: string
  admin_email: string
  admin_password: string
}

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', data)
    return response.data
  },

  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/register', data)
    return response.data
  },

  me: async () => {
    const response = await apiClient.get('/auth/me')
    return response.data
  },

  orgInfo: async (slug: string): Promise<OrgInfo> => {
    const response = await apiClient.get<OrgInfo>(`/auth/org-info/${slug}`)
    return response.data
  },

  slackOAuthUrl: async (orgSlug?: string): Promise<{ url: string }> => {
    const query = orgSlug ? `?org=${encodeURIComponent(orgSlug)}` : ''
    const response = await apiClient.get(`/auth/slack/url${query}`)
    return response.data
  },

  slackCallback: async (code: string, state: string): Promise<SlackAuthResult> => {
    const response = await apiClient.post<SlackAuthResult>('/auth/slack/callback', { code, state })
    return response.data
  },

  slackSelectOrg: async (selection_token: string, org_slug: string): Promise<SlackAuthResult> => {
    const response = await apiClient.post<SlackAuthResult>('/auth/slack/select', {
      selection_token,
      org_slug,
    })
    return response.data
  },

  // Global email + password login. Resolves to one org, a picker, or a platform
  // super-admin session (status === 'platform').
  discoverLogin: async (email: string, password: string): Promise<SlackAuthResult> => {
    const response = await apiClient.post<SlackAuthResult>('/auth/login/discover', { email, password })
    return response.data
  },

  // Feature modules enabled for the current user's org (drives nav gating).
  getFeatures: async (): Promise<string[]> => {
    const response = await apiClient.get<{ features: string[] }>('/auth/features')
    return response.data.features
  },

  setPassword: async (password: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.post<{ ok: boolean }>('/auth/set-password', { password })
    return response.data
  },

  registerOrg: async (data: RegisterOrgRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/register-org', data)
    return response.data
  },

  searchOrgs: async (q: string, limit = 10): Promise<{ slug: string; name: string }[]> => {
    const response = await apiClient.get<{ slug: string; name: string }[]>('/auth/search-orgs', {
      params: { q, limit },
    })
    return response.data
  },
}
